import '@/maplibreWorker'
import './i18n'
import './styles/tailwind.css'
import 'react-loading-skeleton/dist/skeleton.css'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'

import environment from '_environments'
import { registerVersionDetection } from '@/utilities/runtime/versionDetection'

import { Provider } from 'react-redux'
import { legacy_createStore as createStore, applyMiddleware } from 'redux'
import { composeWithDevTools } from '@redux-devtools/extension'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { QueryClientProvider } from '@tanstack/react-query'
import { MapProvider } from 'react-map-gl/maplibre'
import { thunk } from 'redux-thunk'
import reducers from './reducers'
import throttledMiddleware from './middleware/throttle'
import dataTableLayerVisibilityMiddleware from './middleware/dataTableLayerVisibility'
import { LanguageProvider } from './contexts/LanguageContext'
// eslint-disable-next-line import/no-cycle
import App from './containers/App/App'

import {
  configureAmplify,
  configureAnalytics,
  configureFontAwesome,
  configureSentry,
} from './services'

import Alert from '@/components/Alert'
import ToastListener from '@/components/ToastListener'
import { AppToaster } from '@/components/AppToaster'

import { queryClient, queryClientManager } from '@/utilities/queryClientManager'
import { registerNearmapProtocol } from '@/utilities/nearmapProtocol'
import { registerImageSourceProtocol } from '@/utilities/imageSourceProtocol'
import { DataConfigProvider } from '@/contexts/DataConfigProvider'
import AnalyticsProvider from '@/contexts/AnalyticsProvider'
import { SidebarUIProvider } from '@/contexts/sidebar-ui-context'
import { ToolsProvider } from '@/contexts/ToolsContext'
import { SmartLayerProvider } from '@/contexts/SmartLayerContext'
import { SmartLayerEditMaskProvider } from '@/contexts/SmartLayerEditMaskContext'
import { EditingLayerFeatureProvider } from '@/contexts/EditingLayerFeatureContext'
import { DrawProvider } from '@/contexts/DrawContext'
import { ImportModalProvider } from '@/contexts/ImportModalContext'

const cleanupVersionDetection = registerVersionDetection()

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    cleanupVersionDetection()
  })
}

// Configure essential services immediately
configureAmplify()
// Icons are required at first render; init synchronously to avoid missing icons
configureFontAwesome()
if (typeof window !== 'undefined') {
  registerNearmapProtocol()
  registerImageSourceProtocol()
}

// Defer heavy services to avoid blocking initial render
const runDeferredInit = () => {
  try {
    configureSentry()
  } catch (err) {
    console.error('Sentry init failed', err)
  }
  try {
    configureAnalytics()
  } catch (err) {
    console.error('Analytics init failed', err)
  }
}

if (
  typeof window !== 'undefined' &&
  typeof window.requestIdleCallback === 'function'
) {
  // supports all browsers except Safari
  window.requestIdleCallback(runDeferredInit, { timeout: 2000 })
} else {
  if (
    // Safari doesn't support requestIdleCallback, use requestAnimationFrame instead
    typeof window !== 'undefined' &&
    typeof window.requestAnimationFrame === 'function'
  ) {
    window.requestAnimationFrame(() => setTimeout(runDeferredInit, 0))
  } else {
    setTimeout(runDeferredInit, 1)
  }
}

const composeEnhancers = composeWithDevTools({
  // Specify name here, actionsDenylist, actionsCreators and other options if needed
  trace: true,
})

const store = createStore(
  reducers,
  composeEnhancers(
    // `thunk` must come first so it intercepts dispatched thunks (e.g. the
    // atomic map-style thunks) before throttle/visibility middleware, which
    // assume plain-object actions. (MAM-795)
    applyMiddleware(
      thunk,
      throttledMiddleware,
      dataTableLayerVisibilityMiddleware
    )
  )
)

const container =
  document.getElementById('root') || document.createElement('div')
const root = createRoot(container)

// Initialize nearMapUsageTracker query client subscription
queryClientManager.nearMapUsageTrackerQueryClientSubscription()
root.render(
  <AnalyticsProvider>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <Provider store={store}>
          <MapProvider>
            <BrowserRouter>
              <DataConfigProvider>
                <SidebarUIProvider>
                  <ImportModalProvider>
                    <ToolsProvider>
                      <SmartLayerProvider>
                        <SmartLayerEditMaskProvider>
                          <EditingLayerFeatureProvider>
                            <DrawProvider>
                              <AppToaster />
                              <Alert />
                              <ToastListener />
                              <App />
                            </DrawProvider>
                          </EditingLayerFeatureProvider>
                        </SmartLayerEditMaskProvider>
                      </SmartLayerProvider>
                    </ToolsProvider>
                  </ImportModalProvider>
                </SidebarUIProvider>
              </DataConfigProvider>
            </BrowserRouter>
          </MapProvider>
        </Provider>
      </LanguageProvider>
      {environment?.isDev ? (
        <div className='fixed bottom-6 right-[75px] z-5000' aria-hidden>
          <ReactQueryDevtools initialIsOpen={false} buttonPosition='relative' />
        </div>
      ) : null}
    </QueryClientProvider>
  </AnalyticsProvider>
)

export { store }
